import React, { FC, useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { BadgeDesign } from '../../../shared/user/badgeDesign';
import { useTranslation } from '../../translations';
import { Fetcher, useCheckboxState, useConvention, useFetcher } from '../../utils';

interface ReprintReasonProps {
  readonly isRequired: boolean;
  readonly reprintReason: string;
  readonly setReprintReason: (reason: string) => void;
}

interface ReprintReasonModalProps {
  readonly currentDesign: BadgeDesign;
  readonly setDesign: (design: BadgeDesign) => void;
  readonly close: () => void;
  readonly save: (reason: string) => void;
}

export const ReprintBadgeModal: FC<ReprintReasonModalProps> = ({
  close,
  save,
  setDesign,
  currentDesign,
}) => {
  const convention = useConvention();
  const isRequired = convention.forceReprintReason;
  const { ts } = useTranslation();
  const designFetcher = useFetcher<BadgeDesign[]>(async () => {
    return await api.getBadgeDesignList();
  });

  const [reprintReason, setReprintReason] = useState<string>(ts('reprint_unspecified'));
  const [showDesigns, setShowDesigns] = useCheckboxState(false);

  const { data } = designFetcher;

  const updateDesign = useCallback(
    (id: number) => {
      if (!data) {
        return;
      }

      const newDesign = data.find((design) => design.id === id);

      if (!newDesign) {
        toast.error(ts('selected_design_not_found_try_refreshing'));
        return;
      }

      setDesign(newDesign);
    },
    [data, setDesign, ts],
  );

  return (
    <Modal className="modal-large hide-print" fade={false} isOpen toggle={close}>
      <ModalHeader toggle={close}>{ts('reprint_badge')}</ModalHeader>
      <ModalBody>
        <ReprintBadge
          isRequired={isRequired}
          reprintReason={reprintReason}
          setReprintReason={setReprintReason}
        />
        <FormGroup check>
          <Input
            checked={showDesigns}
            id="designOverride"
            onChange={setShowDesigns}
            type="checkbox"
          />
          <Label check for="designOverride">
            {ts('reprint_override')}
          </Label>
        </FormGroup>
        {showDesigns && (
          <>
            {!designFetcher.success && <Fetcher inline result={designFetcher} />}
            {designFetcher.data && (
              <>
                <Label for="designToUse">{ts('design')}:</Label>{' '}
                <Input
                  defaultValue={currentDesign.id}
                  id="designToUse"
                  onChange={(e) => {
                    updateDesign(Number.parseInt(e.currentTarget.value, 10));
                  }}
                  type="select"
                >
                  {designFetcher.data.map((design) => (
                    <option key={design.id} value={design.id}>
                      {design.name}
                    </option>
                  ))}
                </Input>
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close}>
          {ts('common.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={isRequired && reprintReason === ts('reprint_unspecified')}
          id="confirmReprint"
          onClick={() => {
            save(reprintReason);
          }}
        >
          {ts('common.print')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ReprintBadge: FC<ReprintReasonProps> = ({ isRequired, reprintReason, setReprintReason }) => {
  const { ts } = useTranslation();

  const reprintReasons: Record<string, string> = {
    unspecified: ts('reprint_unspecified'),
    bad_print: ts('bad_print'),
    lost: ts('reprint_reason_lost'),
    misspelled: ts('reprint_reason_misspelled'),
    type: ts('reprint_reason_incorrect_type'),
    other: ts('common.other'),
  };

  const [initial, setInitial] = useState(true);
  const reprintReasonOtherRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <p>
        {ts(
          'reprint_reason_card_header',
          ts(isRequired ? 'common.required' : 'common.recommended'),
        )}
      </p>
      <FormGroup>
        {Object.entries(reprintReasons).map(([code, reason]) => {
          const id = `reprintReason${code}`;

          return (
            <FormGroup check className="mb-10" key={code}>
              <Input
                checked={
                  reprintReason === reason ||
                  (code === 'other' &&
                    !initial &&
                    !Object.values(reprintReasons).includes(reprintReason))
                }
                id={id}
                name="reprintReason"
                onChange={() => {
                  setInitial(false);
                  if (code === 'other') {
                    setReprintReason('');
                    reprintReasonOtherRef.current?.focus();
                  } else {
                    setReprintReason(reason);
                  }
                }}
                type="radio"
                value={reason}
              />
              <Label check for={id}>
                {reason}
              </Label>
            </FormGroup>
          );
        })}

        <input
          className="form-control"
          id="reprintReasonOther"
          name="reprintReasonOther"
          onChange={(e) => {
            setReprintReason(e.target.value);
          }}
          placeholder={ts('reprint_reason_enter_other')}
          ref={reprintReasonOtherRef}
          type="text"
          value={reprintReason}
        />
      </FormGroup>
    </>
  );
};
